import {
    createSlice,
    createAsyncThunk,
    createEntityAdapter,
} from "@reduxjs/toolkit";
import { request } from "@/utils/api";

// Create entity adapter
const ndrAdapter = createEntityAdapter({
    selectId: (ndr) => ndr._id,
    sortComparer: (a, b) => new Date(b.createdAt) - new Date(a.createdAt),
});

// Async Thunks
export const fetchNDRData = createAsyncThunk(
    "ndr/fetch",
    async ({ startDate, endDate }, { rejectWithValue }) => {
        try {
            const response = await request({
                endpoint: "/admin/getAllNdr",
                method: "GET",
                params: { startDate, endDate },
            });

            if (!response.success || !Array.isArray(response.data)) {
                return rejectWithValue(response.error.message || "Failed to fetch NDRs");
            }

            return response.data;
        } catch ({ error }) {
            return rejectWithValue(error.message || "Failed to fetch NDRs");
        }
    }
);

export const resolveNDR = createAsyncThunk(
    "ndr/resolve",
    async (data, { rejectWithValue }) => {
        try {
            const response = await request({
                endpoint: `/admin/NDRAction`,
                method: "POST",
                body: data,
            });

            if (!response.success) {
                return rejectWithValue(response.error.message || "Failed to resolve NDR");
            }

            return response.data;
        } catch ({ error }) {
            return rejectWithValue(error.message || "Failed to resolve NDR");
        }
    }
);

export const escalateNDR = createAsyncThunk(
    "ndr/escalate",
    async (ndrs, { rejectWithValue }) => {
        try {
            const response = await request({
                endpoint: `/admin/escalateNDR`,
                method: "PATCH",
                body: ndrs,
            });

            if (!response.success) {
                return rejectWithValue(response.error.message || "Failed to escalate NDRs");
            }

            return response.data;
        } catch ({ error }) {
            return rejectWithValue(error.message || "Failed to escalate NDRs");
        }
    }
);

export const uploadNDRs = createAsyncThunk(
    "ndr/upload",
    async ({ file }, { rejectWithValue }) => {
        try {
            const formData = new FormData();
            formData.append("ndr", file);

            const response = await request({
                endpoint: "/admin/uploadNDR",
                method: "POST",
                body: formData,
                headers: { "Content-Type": "multipart/form-data" },
            });

            if (!response.success) {
                return rejectWithValue(response.error || "Failed to upload NDRs");
            }

            return response.data;
        } catch (error) {
            return rejectWithValue(error.message || "Failed to upload NDRs");
        }
    }
);

// Initial state
const initialState = ndrAdapter.getInitialState({
    loading: false,
    error: null,
});

// Slice
const ndrSlice = createSlice({
    name: "ndr",
    initialState,
    reducers: {
        clearNDRs: (state) => {
            ndrAdapter.removeAll(state);
            state.loading = false;
            state.error = null;
        },
        addAllNDRsIO: (state, action) => {
            ndrAdapter.setAll(state, action.payload);
        },
        addNDROneIO: (state, action) => {
            ndrAdapter.addOne(state, action.payload);
        },
        updateNDROneIO: (state, action) => {
            ndrAdapter.updateOne(state, action.payload);
        },
        deleteNDROneIO: (state, action) => {
            ndrAdapter.removeOne(state, action.payload);
        },
    },
    extraReducers: (builder) => {
        builder
            // Fetch NDRs
            .addCase(fetchNDRData.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchNDRData.fulfilled, (state, action) => {
                ndrAdapter.setAll(state, action.payload);
                state.loading = false;
            })
            .addCase(fetchNDRData.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            // Resolve NDR
            .addCase(resolveNDR.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(resolveNDR.fulfilled, (state, action) => {
                ndrAdapter.upsertMany(state, action.payload);
                state.loading = false;
            })
            .addCase(resolveNDR.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            // Escalate NDR
            .addCase(escalateNDR.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(escalateNDR.fulfilled, (state, action) => {
                if (Array.isArray(action.payload)) {
                    ndrAdapter.upsertMany(state, action.payload);
                } else {
                    ndrAdapter.upsertOne(state, action.payload);
                }
                state.loading = false;
            })
            .addCase(escalateNDR.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            // Upload NDRs
            .addCase(uploadNDRs.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(uploadNDRs.fulfilled, (state, action) => {
                state.loading = false;
            })
            .addCase(uploadNDRs.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            });
    },
});

// Update selectors export
export const {
    selectAll: selectAllNDRs,
    selectById: selectNDRById,
    selectIds: selectNDRIds,
} = ndrAdapter.getSelectors((state) => state.ndr);

// Update actions export
export const { clearNDRs, addAllNDRsIO, addNDROneIO, updateNDROneIO, deleteNDROneIO } = ndrSlice.actions;
export default ndrSlice.reducer;
